import { useVirtualizer } from '@tanstack/react-virtual';
import { type RefObject, useCallback, useMemo, useRef } from 'react';
import { hasVolumeDiscount } from '../../../../data/product';
import { type FuseSearchField, useFuseSearch } from '../../../../hooks/use-fuse-search';
import { useScrollPreservation } from '../../../../hooks/use-scroll-preservation';
import type { Family } from '../data/family';
import type { ProductOrderItem } from '../data/product-order-item';
import { useFamilies } from '../hooks/use-families';
import { useMaxDeals } from '../hooks/use-max-deals';
import { useScrollToProduct } from '../hooks/use-scroll-to-product';
import { aggregateCommitted } from '../utils/family-aggregates';
import { byPriorityDesc } from '../utils/family-priority';
import { DealsSummaryBanner } from './deals-summary-banner';
import { FamilyCard } from './family-card';

const CHROME_PX = 140;
const ROW_PX = 72;

type Props = {
  orderItems: ProductOrderItem[];
  searchQuery: string;
  setOrderItem: (item: ProductOrderItem) => void;
  // Comes from the view model rather than being built here: the allocation is
  // the server's (the allocate endpoint), and this view has no business
  // knowing where the arithmetic happens.
  allocateDeal: (family: Family, targetCases: number) => void;
  onAddProductClicked: (family: Family) => void;
  onRemoveMember: (item: ProductOrderItem) => void;
  scrollOffsetsStore?: RefObject<Record<string, number>>;
};

type SearchableFamily = Family & { searchText: string; itemCodes: string };

const FAMILY_SEARCH_FIELDS: FuseSearchField<SearchableFamily>[] = [
  { name: 'searchText' },
  { name: 'itemCodes', exact: true },
];

const getFamilyKey = (f: SearchableFamily) => f.id;

export function DealsFamilyView({
  orderItems,
  searchQuery,
  setOrderItem,
  allocateDeal,
  onAddProductClicked,
  onRemoveMember,
  scrollOffsetsStore,
}: Props) {
  const allFamilies = useFamilies(orderItems);
  // `.some` not `.every`: non-deal siblings render alongside deal members as context.
  const families = useMemo(
    () => allFamilies.filter((f) => f.members.some((m) => hasVolumeDiscount(m.product))),
    [allFamilies]
  );

  const searchableFamilies = useMemo<SearchableFamily[]>(
    () => families.map(toSearchable),
    [families]
  );
  const matchesQuery = useFuseSearch(
    searchQuery,
    searchableFamilies,
    FAMILY_SEARCH_FIELDS,
    getFamilyKey
  );

  const visibleFamilies = useMemo(() => {
    const filtered = searchableFamilies.filter(matchesQuery);

    return [...filtered].sort(byPriorityDesc);
  }, [searchableFamilies, matchesQuery]);

  const aggregates = useMemo(() => aggregateCommitted(visibleFamilies), [visibleFamilies]);

  // Shared deal-column count across every card. Computed over the full input
  // (not just the search-filtered list) so the column count doesn't jump as
  // the user filters — and so this memo doesn't bust on every keystroke.
  const tierSlotCount = useMaxDeals(orderItems);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Card height is a pure function of (family.id, members.length, tierSlotCount):
  // same family + same SKU count + same tier-bar slot count → same height. Cache
  // measured heights so ResizeObserver-driven remeasures during typing don't pay
  // a getBoundingClientRect (and forced layout) per visible card. The cache key
  // includes every input that can change height, so stale entries are simply
  // never hit again — no invalidation needed.
  const heightCacheRef = useRef<Map<string, number>>(new Map());

  const virtualizer = useVirtualizer({
    count: visibleFamilies.length,
    getScrollElement: () => scrollRef.current,
    getItemKey: (index) => visibleFamilies[index].id,
    estimateSize: (index) => CHROME_PX + visibleFamilies[index].members.length * ROW_PX,
    overscan: 8,
    measureElement: (el) => {
      const key = el.getAttribute('data-height-key');
      if (key) {
        const cached = heightCacheRef.current.get(key);
        if (cached !== undefined) {
          return cached;
        }

        const measured = el.getBoundingClientRect().height;
        heightCacheRef.current.set(key, measured);

        return measured;
      }

      return el.getBoundingClientRect().height;
    },
    // Same ResizeObserver-loop guard as the sales journal: defer the size
    // correction to a requestAnimationFrame so it lands outside the observer's
    // delivery cycle. It also gives the scroll-offset guard below one more
    // frame for a pending scroll event to land before it has to judge whether
    // the DOM and the virtualizer agree.
    useAnimationFrameWithResizeObserver: true,
  });
  // The virtualizer's size-change adjustment uses `getScrollOffset() + delta`
  // as the new scroll target. `getScrollOffset()` only updates on browser
  // scroll events (async), so during programmatic scrolls (search snap-to-0,
  // toggle-back restore) ResizeObserver remeasurements race that update —
  // adjusting against a stale offset shifts scroll to nonsense values. When
  // the DOM `scrollTop` and the virtualizer's cached offset disagree, skip
  // the adjustment entirely: the layout will settle once the scroll event
  // lands and the virtualizer catches up.
  virtualizer.shouldAdjustScrollPositionOnItemSizeChange = (item, _delta, instance) => {
    const element = instance.scrollElement;
    if (!element) {
      return false;
    }

    const domScrollTop = element.scrollTop;
    const cachedOffset = instance.scrollOffset ?? 0;
    if (Math.abs(domScrollTop - cachedOffset) > 1) {
      return false;
    }

    return item.start < domScrollTop;
  };

  const { onScroll } = useScrollPreservation(scrollRef, searchQuery !== '', {
    viewKey: 'deals',
    virtualizer,
    store: scrollOffsetsStore,
  });

  const scrollToProduct = useCallback(
    (productId: string) => {
      const index = visibleFamilies.findIndex((family) =>
        family.members.some((member) => member.product.id === productId)
      );
      if (index < 0) {
        return false;
      }

      virtualizer.scrollToIndex(index, { align: 'start' });

      return true;
    },
    [visibleFamilies, virtualizer]
  );

  useScrollToProduct({ scrollRef, scrollToProduct });

  return (
    <div ref={scrollRef} onScroll={onScroll} className="h-full overflow-auto">
      <DealsSummaryBanner aggregates={aggregates} />
      {visibleFamilies.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-500">No matching deals.</div>
      ) : (
        <div className="relative" style={{ height: `${virtualizer.getTotalSize()}px` }}>
          {virtualizer.getVirtualItems().map((row) => {
            const family = visibleFamilies[row.index];

            return (
              <div
                key={family.id}
                data-index={row.index}
                data-height-key={`${family.id}:${family.members.length}:${tierSlotCount}`}
                ref={virtualizer.measureElement}
                className="absolute left-0 right-0 px-3 py-1"
                style={{ transform: `translateY(${row.start}px)` }}
              >
                <FamilyCard
                  family={family}
                  tierSlotCount={tierSlotCount}
                  setOrderItem={setOrderItem}
                  allocate={allocateDeal}
                  onRemoveMember={onRemoveMember}
                  onAddProductClicked={onAddProductClicked}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function toSearchable(family: Family): SearchableFamily {
  const texts: string[] = [family.name];
  const codes: string[] = [];
  for (const m of family.members) {
    const { product } = m;
    texts.push(product.brand, product.description);
    if (product.item_code) {
      codes.push(product.item_code);
    }

    if (product.suggested_item_code) {
      codes.push(product.suggested_item_code);
    }
  }

  return { ...family, searchText: texts.join(' '), itemCodes: codes.join(' ') };
}
